import { useCallback } from "react";

import joinWithCommas from "shared/utils/joinWithCommas";

import { printBlocksToCassettePrinterAsync } from "app/actions/blockActions";
import { useAppDispatch } from "app/hooks";
import { CasesState } from "app/reducers/casesWithWorkReducer";
import { useSelectedLabStation } from "hooks/useSelectedLabStation";

export const printCassetteBlockNames = (
  { casesWithWork }: { casesWithWork: CasesState },
  ids: number[]
) => {
  const casesWithWorkArray = Object.values(casesWithWork);
  const blocksByAccession: { [key: string]: string[] } = {};
  ids.forEach((id) => {
    const pathCase = casesWithWorkArray.find((c) =>
      c.blocks.some((b) => b.id === id)
    );
    const block = pathCase?.blocks.find((b) => b.id === id);
    const specimen = pathCase?.specimens.find(
      (s) => s.id === block?.specimen_id
    );
    if (!pathCase?.accession) return;
    if (!blocksByAccession[pathCase.accession])
      blocksByAccession[pathCase.accession] = [];
    blocksByAccession[pathCase.accession].push(
      `${specimen?.name || ""}${block?.name || ""}`
    );
  });
  const sortedAccessions = Object.keys(blocksByAccession);
  sortedAccessions.sort();
  return sortedAccessions
    .map(
      (accession) =>
        `${accession} ${joinWithCommas(blocksByAccession[accession])}`
    )
    .join("\n");
};

export const useBarcodePrintingCallbacks = () => {
  const station = useSelectedLabStation();
  const dispatch = useAppDispatch();

  const printDocument = useCallback((id: number | number[], caseId: number) => {
    const ids = [id].flat();
    if (!ids.length) return;

    window.open(`/print/${caseId}/?documents=${ids.join(",")}`);
  }, []);

  const printSpecimen = useCallback((id: number | number[], caseId: number) => {
    const ids = [id].flat();
    if (!ids.length) return;

    window.open(`/print/${caseId}/?specimens=${ids.join(",")}`);
  }, []);

  const printBlock = useCallback(
    (id: number | number[], caseId: number, apiPrint = false) => {
      const ids = [id].flat();
      if (!ids.length) return;

      const browserPrint = () =>
        window.open(`/print/${caseId}/?blocks=${ids.join(",")}`);

      if (!apiPrint) {
        browserPrint();
      } else {
        dispatch(
          printBlocksToCassettePrinterAsync({
            parentLookupLabStationId: station.id,
            requestBody: { block_ids: ids },
          })
        ).catch(() => {
          browserPrint();
        });
      }
    },
    [station.id]
  );

  const printSlide = useCallback((id: number | number[], caseId: number) => {
    const ids = [id].flat();
    if (!ids.length) return;

    window.open(`/print/${caseId}/?slides=${ids.join(",")}&printer=espo`);
  }, []);

  return {
    printDocument,
    printSpecimen,
    printBlock,
    printSlide,
  };
};
